import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useVelocity, useSpring, useMotionValue, useTransform } from 'framer-motion';

import { MOODS, ACCENTS } from './constants/theme';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useSoundEngine } from './hooks/useSoundEngine';

import { IgnitionSequence } from './components/IgnitionSequence';
import { RacingBackground } from './components/RacingBackground';
import { StealthControls } from './components/StealthControls';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';

import { Home } from './pages/Home';
import { About } from './pages/About';
import { Skills } from './pages/Skills';
import { Projects } from './pages/Projects';
import { Experience } from './pages/Experience';
import { Education } from './pages/Education';
import { Contact } from './pages/Contact';

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isResolving, setIsResolving] = useState(false);
  const [activePage, setActivePage] = useState('HOME');
  const [mood, setMood] = useState(MOODS.AURA);
  const [accent, setAccent] = useState(ACCENTS.BLUE);
  const [isMuted, setIsMuted] = useState(false);
  const [triggerHero, setTriggerHero] = useState(0);
  const { playClick, playSwoosh, playIgnition, setEngineVelocity } = useSoundEngine(isMuted);
  const reducedMotion = usePrefersReducedMotion();
  const scrollY = useMotionValue(0);
  const scrollVelocity = useVelocity(scrollY);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);

  const velocityRef = useRef(0);

  const navRefs = useRef({});

  useEffect(() => {
    let hideTimeout;
    let ticking = false;

    const updateScrollState = () => {
      ticking = false;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;

      scrollY.set(scrollTop);
      setScrollProgress(progress);

      if (scrollTop > 5) {
        setShowProgress(true);
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => setShowProgress(false), 900);
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateScrollState);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(hideTimeout);
    };
  }, [scrollY]);

  useEffect(() => {
    const savedMood = localStorage.getItem("mood");
    const savedAccent = localStorage.getItem("accent");
    if (savedMood && MOODS[savedMood]) setMood(MOODS[savedMood]);
    if (savedAccent && ACCENTS[savedAccent]) setAccent(ACCENTS[savedAccent]);
  }, []);

  useEffect(() => {
    localStorage.setItem("mood", mood.name);
  }, [mood]);

  useEffect(() => {
    const accentKey = Object.keys(ACCENTS).find(key => ACCENTS[key] === accent);
    if (accentKey) localStorage.setItem("accent", accentKey);
  }, [accent]);

  const cameraShake = useSpring(0, { stiffness: 1000, damping: 50 });
  const shakeDirRef = useRef({ x: Math.random() - 0.5, y: Math.random() - 0.5 });

  useEffect(() => {
    return scrollVelocity.onChange((v) => {
      const absV = Math.min(Math.abs(v), 2400);
      velocityRef.current = absV;
      setEngineVelocity(absV);
      cameraShake.set(reducedMotion ? 0 : absV / 100);
    });
  }, [scrollVelocity, setEngineVelocity, cameraShake, reducedMotion]);

  const navigate = useCallback((page, idx) => {
    if (page === activePage || isResolving) return;
    const pan = (idx - 1.5) / 1.5;
    playSwoosh(pan);
    setIsResolving(true);
    setTimeout(() => {
      setActivePage(page);
      setTriggerHero(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setIsResolving(false), 400);
    }, 150);
  }, [activePage, isResolving, playSwoosh]);

  const cycleMood = useCallback(() => {
    setMood((current) => {
      if (current.name === 'VOID') return MOODS.AURA;
      if (current.name === 'AURA') return MOODS.PULSE;
      return MOODS.VOID;
    });
  }, []);

  const cycleAccent = useCallback(() => {
    setAccent((current) => {
      if (current.name === 'Racing Red') return ACCENTS.BLUE;
      if (current.name === 'Electric Blue') return ACCENTS.PURPLE;
      return ACCENTS.RED;
    });
  }, []);

  const magnetX = useSpring(0, { stiffness: 300, damping: 20 });
  const magnetY = useSpring(0, { stiffness: 300, damping: 20 });

  const handleMagnetMove = useCallback((e, connectRef) => {
    const rect = connectRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    magnetX.set(x * 0.25);
    magnetY.set(y * 0.25);
  }, [magnetX, magnetY]);

  const resetMagnet = useCallback(() => {
    magnetX.set(0);
    magnetY.set(0);
  }, [magnetX, magnetY]);

  useEffect(() => {
    const el = navRefs.current[activePage];
    if (el && window.innerWidth < 768) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activePage]);

  return (
    <>
    <AnimatePresence>
      {showProgress && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed top-0 left-0 h-[2px] z-[9999]"
          style={{
            width: `${scrollProgress * 100}%`,
            backgroundColor: accent.color,
            boxShadow: `0 0 8px ${accent.color}`
          }}
        />
      )}
    </AnimatePresence>

    <motion.div style={{ x: useTransform(cameraShake, s => s * shakeDirRef.current.x), y: useTransform(cameraShake, s => s * shakeDirRef.current.y) }} className={`min-h-screen ${mood.bg} text-white font-sans selection:bg-white selection:text-black transition-colors duration-1000 overflow-x-hidden`}>
      <AnimatePresence> {isLoading && <IgnitionSequence key="ignition" accent={accent} mood={mood} playIgnition={playIgnition} onComplete={() => setIsLoading(false)} reducedMotion={reducedMotion} />} </AnimatePresence>
      <RacingBackground mood={mood} scrollSpeedRef={velocityRef} accentColor={accent.color} triggerHero={triggerHero} reducedMotion={reducedMotion} />

      <Navbar
        accent={accent}
        activePage={activePage}
        navigate={navigate}
        navRefs={navRefs}
        reducedMotion={reducedMotion}
        magnetX={magnetX}
        magnetY={magnetY}
        onMagnetMove={handleMagnetMove}
        onMagnetReset={resetMagnet}
        playIgnition={playIgnition}
        playClick={playClick}
      />

      <motion.main initial={{ opacity: 0 }} animate={{ opacity: isLoading ? 0 : 1 }} transition={{ delay: 0.6, duration: 0.8 }} className="relative z-10">
        <AnimatePresence mode="wait">
          {activePage === 'HOME' && <Home key="home" accent={accent} mood={mood} isResolving={isResolving} />}
          {activePage === 'ABOUT' && <About key="about" accent={accent} mood={mood} isResolving={isResolving} />}
          {activePage === 'SKILLS' && <Skills key="skills" accent={accent} mood={mood} isResolving={isResolving} />}
          {activePage === 'PROJECTS' && <Projects key="projects" accent={accent} mood={mood} isResolving={isResolving} playClick={playClick} />}
          {activePage === 'EXPERIENCE' && <Experience key="experience" accent={accent} mood={mood} />}
          {activePage === 'EDUCATION' && <Education key="education" accent={accent} mood={mood} />}
          {activePage === 'CONTACT' && <Contact key="contact" accent={accent} mood={mood} />}
        </AnimatePresence>
      </motion.main>

      {!isLoading && (
        <StealthControls
          mood={mood}
          accent={accent}
          cycleMood={cycleMood}
          cycleAccent={cycleAccent}
          isMuted={isMuted}
          setIsMuted={setIsMuted}
          scrollVelocity={scrollVelocity}
          playClick={playClick}
        />
      )}

      <Footer isMuted={isMuted} playIgnition={playIgnition} />

      <div className="fixed inset-0 pointer-events-none opacity-[0.025] z-[100] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
    </motion.div>
    </>
  );
}
